import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";
import { Decimal } from "decimal.js";
import { toNumber } from "lodash";

import { SeparableAccumulationItem } from "@/App/api/member";
import { ScreenReader } from "@/App/components";
import { AccumulatorProgressBar } from "@/App/components/AccumulatorProgressBar";
import { DisplayScopes, MoneyAccumulationValues } from "@/App/types/accumulation";
import { formatDollars } from "@/App/utils/utils";
import { DEDUCTIBLE, OUT_OF_POCKET, PLAN_MAXIMUM } from "@/App/hooks/accumulations";

import "./AccumulatorCard.scss";

export interface AccumulatorWidgetCard {
  displayScope: DisplayScopes;
  accumulator: SeparableAccumulationItem;
}

export const AccumulatorWidgetCard: FC<AccumulatorWidgetCard> = ({ displayScope, accumulator }) => {
  const { t: translate } = useTranslation();

  const title = (type: string, name: string) => {
    switch (type) {
      case DEDUCTIBLE:
        return translate("ACCUMULATOR_CARD.deductible");
      case OUT_OF_POCKET:
        return translate("ACCUMULATOR_CARD.outOfPocket");
      case PLAN_MAXIMUM:
        return translate("ACCUMULATOR_CARD.planMaximum");
      case "plan_pay_fertility":
        return translate("ACCUMULATOR_CARD.fertilityMaximum");
      default:
        return name ? name : type;
    }
  };

  const limitReachedLabel = (type: string) => {
    switch (type) {
      case "plan_maximum":
        return translate("ACCUMULATOR_CARD.planLimitReached");
      default:
        return null;
    }
  };

  const noAccumulatorDescription = useMemo(() => {
    if (accumulator.type === DEDUCTIBLE) {
      return translate("ACCUMULATOR_CARD.noAccumulatorDescriptionDeductible");
    } else if (accumulator.type === OUT_OF_POCKET) {
      return translate("ACCUMULATOR_CARD.noAccumulatorDescriptionOutOfPocket");
    } else if (accumulator.type === PLAN_MAXIMUM) {
      return translate("ACCUMULATOR_CARD.noAccumulatorDescriptionPlanMaximum");
    } else {
      return `There is no maximum for this accumulator ${accumulator.type}`;
    }
  }, [accumulator]);

  const limitReached = useMemo(() => {
    if (displayScope === DisplayScopes.Family) {
      return toNumber(accumulator.family_maximum) > 0 && toNumber(accumulator.family_accumulated) >= toNumber(accumulator.family_maximum);
    } else if (displayScope === DisplayScopes.Individual) {
      return (
        toNumber(accumulator.individual_maximum) > 0
        && toNumber(accumulator.individual_accumulated) >= toNumber(accumulator.individual_maximum)
      );
    }
  }, [displayScope, accumulator]);

  const maximum = useMemo(() => {
    if (displayScope === DisplayScopes.Family) {
      return toNumber(accumulator.family_maximum);
    } else if (displayScope === DisplayScopes.Individual) {
      return toNumber(accumulator.individual_maximum);
    }
  }, [displayScope, accumulator]);

  const spent = useMemo(() => {
    if (displayScope === DisplayScopes.Family) {
      return toNumber(accumulator.family_accumulated);
    } else if (displayScope === DisplayScopes.Individual) {
      return toNumber(accumulator.individual_accumulated);
    }
  }, [displayScope, accumulator]);

  const remaining = useMemo(() => {
    if (displayScope === DisplayScopes.Family) {
      return toNumber(accumulator.family_maximum) - toNumber(accumulator.family_accumulated);
    } else if (displayScope === DisplayScopes.Individual) {
      return toNumber(accumulator.individual_maximum) - toNumber(accumulator.individual_accumulated);
    }
  }, [displayScope, accumulator]);

  return (
    <Stack spacing="medium">
      <Stack spacing="small">
        <Subheading textAlign="left">
          <ScreenReader>{translate("ACCUMULATOR_CARD.deductibleInformation")}</ScreenReader>
          <div data-testid={`div-test__${accumulator.name}-title`}>{title(accumulator.type, accumulator.name)}</div>
        </Subheading>
      </Stack>
      <Stack spacing="small" className="accumulator-progress-section">
        <Stack spacing="none">
          {limitReached && <Body>{limitReachedLabel(accumulator.type) ?? translate("ACCUMULATOR_CARD.limitHasBeenReached")}</Body>}
          {!limitReached && (
            <Body>
              {maximum > 0 ? translate("ACCUMULATOR_CARD.yourLimitIs") + " " + formatDollars(maximum.toString()) : noAccumulatorDescription}
            </Body>
          )}
        </Stack>
        {maximum > 0 && (
          <AccumulatorProgressBar
            accumulator={
              {
                spent: new Decimal(spent),
                max: new Decimal(maximum),
                remaining: new Decimal(remaining),
              } as MoneyAccumulationValues
            }
            spentHelpText={translate("ACCUMULATOR_CARD.spent")}
            remainingHelpText={translate("ACCUMULATOR_CARD.remaining")}
            limitReached={limitReached}
            limitReachedWarning={limitReached}
            iconSize="small"
          />
        )}
      </Stack>
    </Stack>
  );
};
